import { useDispatch, useSelector } from "react-redux";
import { useEffect } from "react";
import Table from "../ui/table/TableController";
import {
  companiesSelector,
  getHouseAccounts,
  getOutlets,
  updatePayment,
  updatePaymentState,
} from "../../redux/slices/companiesSlice";
import { authSelector } from "../../redux/slices/authSlice";
import { uiSelector } from "../../redux/slices/uiSlice";
import Loading from "./tableState/Loading";
import NoSearch from "./tableState/NoSearch";
import NoItems from "./tableState/NoItems";
import { isSlgAnaLogin } from "../../utils/authRules";
import {
  getIndexedFieldName,
  getItemizerCodesForToken,
} from "../../utils/itemizerRules";

const POS_SECTION = {
  background: "#eef4fd",
  color: "#346fcf",
  dividerColor: "#88aef5",
};

const PMS_SECTION = {
  background: "#f4effb",
  color: "#7b68b3",
  dividerColor: "#88aef5",
};

const PAYMENT_FIELD_GROUPS = [
  { base: "pms_id", shortLabel: "HA", fullLabel: "House Account" },
  { base: "reservation_id", shortLabel: "Res", fullLabel: "Reservation ID" },
  { base: "pms_account_id", shortLabel: "PMS", fullLabel: "PMS Account ID" },
];

const Payments = () => {
  const {
    payments,
    tableLoading,
    cardTypesLoading,
    houseAccountsLoading,
    cardTypes,
    houseAccounts,
    outlets,
    outletsLoading,
    currentId,
  } = useSelector(companiesSelector);

  const dispatch = useDispatch();
  const { searchQuery } = useSelector(uiSelector);
  const { token, integraInfo } = useSelector(authSelector);
  const paymentIntegraInfo = String(
    payments?.xintegra ||
      payments?.find?.((payment) => payment?.xintegra)?.xintegra ||
      payments?.[0]?.xintegra ||
      integraInfo ||
      ""
  ).toUpperCase();
  const isMews = paymentIntegraInfo === "MEWSMICROS";
  const isWristcoinMews = paymentIntegraInfo === "WRISTCOINMEWS";
  const isShopifyOhip = paymentIntegraInfo === "SHOPIFYOHIP";
  const showSpecialPaymentCombinations = isSlgAnaLogin({ token, integraInfo });
  const itemizerCodes = getItemizerCodesForToken({ token });
  const hideDeferredPaymentFields =
    isWristcoinMews || isShopifyOhip || showSpecialPaymentCombinations;

  useEffect(() => {
    if (isMews && currentId) {
      dispatch(getOutlets());
    }
    if (isWristcoinMews && currentId) {
      dispatch(getHouseAccounts());
    }
  }, [currentId, dispatch, isMews, isWristcoinMews]);

  const houseAccountOptions = houseAccounts.map((houseAccount) => {
    const source =
      houseAccount.house_name ||
      `${houseAccount.pms_id || ""}|${houseAccount.pms_name || ""}`;
    const [rawId, ...rawNameParts] = String(source).split("|");
    const id = rawId.trim();
    const name = rawNameParts.join("|").trim();

    return {
      label: name ? `${id}|${name}` : id,
      value: id,
    };
  });

  const outletOptions = (outlets || [])
    .map((outlet) => {
      const value =
        outlet?.id ??
        outlet?.outlet_id ??
        outlet?.floor_id ??
        outlet?.pms_id ??
        "";
      const name =
        outlet?.name ??
        outlet?.outlet_name ??
        outlet?.floor_name ??
        outlet?.pms_name ??
        "";

      return value === ""
        ? null
        : {
            label: name ? `${value} - ${name}` : String(value),
            value,
            name: String(name || ""),
          };
    })
    .filter(Boolean);
  const outletNameById = new Map(
    outletOptions.map((option) => [String(option.value), option.name])
  );
  const houseAccountLabelById = new Map(
    houseAccountOptions.map((option) => [String(option.value), option.label])
  );
  const cardTypeOptions = cardTypes.map((cardType) => ({
    label: cardType.cardcode,
    value: cardType.cardcode,
  }));
  const paymentMethodOptions = isMews
    ? houseAccountOptions.map((option) => ({
        ...option,
        value: option.label,
      }))
    : houseAccountOptions;

  const specialPaymentColumns = itemizerCodes.flatMap((code, index) =>
    PAYMENT_FIELD_GROUPS.map((fieldGroup) => ({
      code,
      field: getIndexedFieldName(fieldGroup.base, index),
      label: `${code} ${fieldGroup.shortLabel}`,
      fullLabel: `${code} ${fieldGroup.fullLabel}`,
      group: fieldGroup.base,
    }))
  );

  const filteredPayments = payments.filter((i) => {
    const search = String(searchQuery || "").toLowerCase();

    return (
      String(i.payment_id).includes(searchQuery) ||
      i.payment_name.toLowerCase().includes(search) ||
      String(i.pms_id).includes(searchQuery) ||
      (showSpecialPaymentCombinations &&
        specialPaymentColumns.some((column) =>
          String(i[column.field] || "").toLowerCase().includes(search)
        ))
    );
  });

  const sectionHeaders = showSpecialPaymentCombinations
    ? [
        {
          id: "pos",
          label: "POS",
          span: 3,
          background: POS_SECTION.background,
          color: POS_SECTION.color,
          dividerAfter: true,
          dividerColor: POS_SECTION.dividerColor,
        },
        ...itemizerCodes.map((code, index) => ({
          id: `pms-${code}`,
          label: code,
          span: PAYMENT_FIELD_GROUPS.length,
          background: PMS_SECTION.background,
          color: PMS_SECTION.color,
          dividerAfter: index < itemizerCodes.length - 1,
          dividerColor: PMS_SECTION.dividerColor,
        })),
        {
          id: "action",
          label: "Action",
          span: 1,
          background: PMS_SECTION.background,
          color: PMS_SECTION.color,
        },
      ]
    : [
        {
          id: "pos",
          label: "POS",
          span: 3,
          background: POS_SECTION.background,
          color: POS_SECTION.color,
          dividerAfter: true,
          dividerColor: POS_SECTION.dividerColor,
        },
        {
          id: "pms",
          label: "PMS",
          span: isMews ? 4 : isWristcoinMews ? 3 : hideDeferredPaymentFields ? 5 : 7,
          background: PMS_SECTION.background,
          color: PMS_SECTION.color,
        },
      ];

  const headerData = showSpecialPaymentCombinations
    ? [
        {
          id: 132,
          label: "Company",
          width: "8%",
          sortable: false,
          section: "pos",
          sectionBackground: POS_SECTION.background,
        },
        {
          id: 232,
          label: "Id",
          width: "6%",
          sortable: true,
          section: "pos",
          sectionBackground: POS_SECTION.background,
        },
        {
          id: 342,
          label: "Name",
          width: "12%",
          sortable: true,
          section: "pos",
          sectionBackground: POS_SECTION.background,
          dividerAfter: true,
          dividerColor: POS_SECTION.dividerColor,
        },
        ...specialPaymentColumns.map((column, index) => ({
          id: 400 + index,
          label: column.label,
          width: "7%",
          sortable: true,
          section: `pms-${column.code}`,
          sectionBackground: PMS_SECTION.background,
          dividerAfter: (index + 1) % PAYMENT_FIELD_GROUPS.length === 0,
          dividerColor: PMS_SECTION.dividerColor,
        })),
        {
          id: 338,
          label: "Action",
          width: "8%",
          sortable: false,
          section: "action",
          sectionBackground: PMS_SECTION.background,
        },
      ]
    : [
        {
          id: 132,
          label: "Company",
          width: "10%",
          sortable: false,
          section: "pos",
          sectionBackground: POS_SECTION.background,
        },
        {
          id: 232,
          label: "Id",
          width: "10%",
          sortable: true,
          section: "pos",
          sectionBackground: POS_SECTION.background,
        },
        {
          id: 342,
          label: "Name",
          width: "16%",
          sortable: true,
          section: "pos",
          sectionBackground: POS_SECTION.background,
          dividerAfter: true,
          dividerColor: POS_SECTION.dividerColor,
        },
        ...(isWristcoinMews
          ? [
              {
                id: 332,
                label: "Outlet",
                width: "10%",
                sortable: true,
                section: "pms",
                sectionBackground: PMS_SECTION.background,
              },
            ]
          : isMews
          ? [
              {
                id: 332,
                label: "Outlet ID",
                width: "14%",
                sortable: true,
                section: "pms",
                sectionBackground: PMS_SECTION.background,
              },
            ]
          : [
              {
                id: 332,
                label: "HOUSE ACCOUNT",
                width: "10%",
                sortable: true,
                section: "pms",
                sectionBackground: PMS_SECTION.background,
              },
              ...(hideDeferredPaymentFields
                ? []
                : [
                    {
                      id: 333,
                      label: "HOUSE ACCOUNT (Deferred)",
                      width: "10%",
                      sortable: true,
                      section: "pms",
                      sectionBackground: PMS_SECTION.background,
                    },
                  ]),
              {
                id: 334,
                label: "RESERVATION ID",
                width: "10%",
                sortable: true,
                section: "pms",
                sectionBackground: PMS_SECTION.background,
              },
              ...(hideDeferredPaymentFields
                ? []
                : [
                    {
                      id: 335,
                      label: "RESERVATION ID (Deferred) ",
                      width: "10%",
                      sortable: true,
                      section: "pms",
                      sectionBackground: PMS_SECTION.background,
                    },
                  ]),
            ]),
        {
          id: 336,
          label: isMews
            ? "Payment Method"
            : isWristcoinMews
            ? "Payment Type"
            : isShopifyOhip
            ? "Payment Method (Opera)"
            : "PMS Account ID",
          width: isMews ? "20%" : "16%",
          sortable: true,
          inputType: "date",
          section: "pms",
          sectionBackground: PMS_SECTION.background,
        },
        ...(!isWristcoinMews
          ? [
              {
                id: 337,
                label: "Start Date (YYYY-MM-DD)",
                width: isMews ? "8%" : "16%",
                sortable: true,
                section: "pms",
                sectionBackground: PMS_SECTION.background,
              },
            ]
          : []),
        {
          id: 338,
          label: "Action",
          width: "16%",
          sortable: false,
          section: "pms",
          sectionBackground: PMS_SECTION.background,
        },
      ];

  const rowKeys = showSpecialPaymentCombinations
    ? [
        "company_id",
        "payment_id",
        "payment_name",
        ...specialPaymentColumns.map((column) => column.field),
      ]
    : [
        "company_id",
        "payment_id",
        "payment_name",
        ...(isWristcoinMews
          ? ["pms_id"]
          : isMews
          ? ["pms_id"]
          : [
              "pms_id",
              ...(hideDeferredPaymentFields ? [] : ["pms_id_deferred"]),
              "reservation_id",
              ...(hideDeferredPaymentFields ? [] : ["reservation_id_deferred"]),
            ]),
        "pms_account_id",
        ...(!isWristcoinMews ? ["start_date"] : []),
      ];

  const editable = showSpecialPaymentCombinations
    ? specialPaymentColumns.map((column) => column.field)
    : [
        ...(isWristcoinMews
          ? ["pms_id"]
          : isMews
          ? ["pms_id"]
          : [
              "pms_id",
              ...(hideDeferredPaymentFields ? [] : ["pms_id_deferred"]),
              ...(isShopifyOhip ? [] : ["reservation_id"]),
              ...(hideDeferredPaymentFields ? [] : ["reservation_id_deferred"]),
            ]),
        "pms_account_id",
        ...(!isWristcoinMews ? ["start_date"] : []),
      ];

  const baseEditableData = {
    pms_id: {
      type: "dropdown",
      allowCreate: true,
      placeholder: isMews || isWristcoinMews ? "Select Outlet" : "Select or type House Account",
      items: isMews ? outletOptions : houseAccountOptions,
    },

    pms_id_deferred: {
      type: "dropdown",
      allowCreate: true,
      placeholder: "Select or type Deferred House Account",
      items: houseAccountOptions,
    },

    pms_account_id: {
      type: "dropdown",
      placeholder: isMews
        ? "Select House Account"
        : isWristcoinMews
        ? "Select Payment Type"
        : "Select PMS Account ID",
      items: isMews ? paymentMethodOptions : cardTypeOptions,
      formatDisplayValue: isMews
        ? (value, payment) =>
            (String(value).includes("|") ? value : undefined) ||
            houseAccountLabelById.get(String(value)) ||
            (payment.pms_name ? `${value}|${payment.pms_name}` : value)
        : undefined,
    },
  };

  const specialEditableData = specialPaymentColumns.reduce((result, column) => {
    if (column.group === "pms_id") {
      return {
        ...result,
        [column.field]: {
          type: "dropdown",
          allowCreate: true,
          placeholder: `${column.code} House Account`,
          items: houseAccountOptions,
        },
      };
    }

    if (column.group === "pms_account_id") {
      return {
        ...result,
        [column.field]: {
          type: "dropdown",
          placeholder: `${column.code} PMS Account ID`,
          items: cardTypeOptions,
        },
      };
    }

    return result;
  }, {});

  const editableData = showSpecialPaymentCombinations
    ? specialEditableData
    : baseEditableData;

  const sortByData = showSpecialPaymentCombinations
    ? [
        {},
        {
          label: "payment_id",
          type: "number",
        },
        {
          label: "payment_name",
          type: "string",
        },
        ...specialPaymentColumns.map((column) => ({
          label: column.field,
          type: column.group === "reservation_id" ? "number" : "string",
        })),
      ]
    : [
        {},
        {
          label: "payment_id",
          type: "number",
        },
        {
          label: "payment_name",
          type: "string",
        },
        ...(isWristcoinMews
          ? [{ label: "pms_id", type: "number" }]
          : isMews
          ? [{ label: "pms_id", type: "number" }]
          : [
              { label: "pms_id", type: "number" },
              ...(hideDeferredPaymentFields
                ? []
                : [{ label: "pms_id_deferred", type: "number" }]),
              { label: "reservation_id", type: "number" },
              ...(hideDeferredPaymentFields
                ? []
                : [{ label: "reservation_id_deferred", type: "number" }]),
            ]),
        {
          label: "pms_account_id",
          type: "number",
        },
        ...(!isWristcoinMews
          ? [
              {
                label: "start_date",
                type: "string",
              },
            ]
          : []),
      ];

  const updateAction = (x) => {
    dispatch(updatePaymentState(x));
    const updatePayload = { ...x };

    if (showSpecialPaymentCombinations) {
      delete updatePayload.pms_id_deferred;
      delete updatePayload.reservation_id_deferred;
      delete updatePayload.start_date;
    }

    if (isMews && x.pms_id && !String(x.pms_id).includes("|")) {
      const outletName = outletNameById.get(String(x.pms_id));
      updatePayload.pms_id = outletName ? `${x.pms_id}|${outletName}` : x.pms_id;
    }

    return dispatch(updatePayment(updatePayload));
  };

  if (
    tableLoading ||
    cardTypesLoading ||
    houseAccountsLoading ||
    (isMews && outletsLoading)
  ) {
    return <Loading />;
  }

  if (filteredPayments.length === 0 && searchQuery) return <NoSearch />;

  if (filteredPayments.length === 0) return <NoItems />;

  return (
    <Table
      sortByData={sortByData}
      headerData={headerData}
      sectionHeaders={sectionHeaders}
      rowKeys={rowKeys}
      data={filteredPayments}
      editable={editable}
      editableData={editableData}
      row_key="payment_id"
      updateAction={updateAction}
    />
  );
};

export default Payments;

